import { Badge, Box, Button, Flex, VisuallyHidden } from '@chakra-ui/react';
import { faPaperclip } from '@fortawesome/pro-regular-svg-icons';
import React from 'react';
import { CustomHeading, Icon } from 'shared-components';
import { ApplicationStatus, ITab } from 'types';
import { useTranslation } from "react-i18next";

export interface Props {
  label?: string;
  tabs: ReadonlyArray<ITab>;
  onTabSelect: (tabId: string) => void;
  isActiveTab?: (tabId: string) => boolean;
  activeTab: string | string[];
  isSearchActive?: boolean;
  filterByMessage?: boolean;
  dataSelector?: string;
  linkAs?: React.ElementType;
}
const TrapTabs: React.FC<Props> = ({
  tabs,
  label,
  onTabSelect,
  isActiveTab,
  activeTab,
  isSearchActive,
  filterByMessage,
  dataSelector,
  linkAs,
}) => {
  if (!activeTab) {
    activeTab = ApplicationStatus.InProgress;
  }

  // Default active tab test unless specified by props.
  if (!isActiveTab) {
    isActiveTab = (tabId: string) => tabId === activeTab;
  }

  return (
    <Flex flexDirection="column" data-selector={`trap-tab-section-${dataSelector}`}>
      {label && (
        <CustomHeading
          as={'div'}
          mb={1}
          px={'sidebarPxSmall'}
          fontSize={'2xs'}
          lineHeight={4}
          color={'muted'}
          isAllCaps
        >
          {label}
        </CustomHeading>
      )}

      {tabs.map((tab, i) => {
        const isActive = isActiveTab(tab.id);

        return (
          <Tab
            key={i}
            {...tab}
            isActive={isActive}
            onTabSelect={onTabSelect}
            isSearchActive={isSearchActive}
            filterByMessage={filterByMessage}
            dataSelector={`trap-tab-${i}`}
            withIcon={tab.withIcon}
            linkAs={linkAs}
          />
        );
      })}
    </Flex>
  );
};

const Tab = ({
  id,
  label,
  count,
  statuses,
  isActive,
  onTabSelect,
  isSearchActive,
  filterByMessage,
  dataSelector,
  withIcon,
  linkAs,
}: {
  id: string;
  label: string;
  count?: number;
  statuses?: string[];
  isActive: boolean;
  onTabSelect?: (tabId: string, statuses: string[]) => void;
  isSearchActive?: boolean;
  filterByMessage?: boolean;
  dataSelector: string;
  withIcon?: boolean;
  linkAs?: React.ElementType;
}) => {
    const { t } = useTranslation('common');
  const onClick = () => {
    if (!isActive) {
      onTabSelect(id, statuses ?? []);
    }
  };

  const getBadgeColour = () => {
    if (isSearchActive === true && count > 0) {
      return {
        colorScheme: 'blue',
        variant: 'vivid-pill',
      };
    } else if (filterByMessage && count > 0) {
      return {
        colorScheme: 'pink',
        variant: 'vivid-pill',
      };
    }
    return {
      variant: 'pill',
    };
  };

  const { colorScheme, variant } = getBadgeColour();

  return (
    <Button
      as={linkAs}
      to={linkAs ? id : undefined}
      variant="sidebar"
      size="sm"
      isActive={isActive}
      onClick={!linkAs ? onClick : undefined}
      data-selector={dataSelector}
    >
      <Box as="span" overflow="hidden" textOverflow="ellipsis">
        {label}
      </Box>

      {!withIcon ? (
        <Badge size="xs" colorScheme={colorScheme} variant={variant}>
          <VisuallyHidden> {t('common:students')} </VisuallyHidden>
          {count ?? 0}
        </Badge>
      ) : (
        count != null &&
        count > 0 && (
          <Badge size="xs" colorScheme="blue" borderRadius="full" px={1.5} gap={0.5}>
            <Icon colorScheme="blue" icon={faPaperclip} size="sm" />
          </Badge>
        )
      )}
    </Button>
  );
};

export default TrapTabs;
